import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { BRAND, CONTACT } from "@/data/content";

const NAV = [
  { label: "Services", href: "#services" },
  { label: "Export DDP", href: "/export-from-india-ddp", isRoute: true },
  { label: "Express Partner", href: "/express-clearance-partner-india", isRoute: true },
  { label: "K-Beauty", href: "/korean-cosmetics-sourcing", isRoute: true },
  { label: "Blog", href: "/blog", isRoute: true },
  { label: "FAQ", href: "#faq" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const resolveHref = (n) => {
    if (n.isRoute) return n.href;
    // hash-links from non-home page should navigate to home + hash
    return onHome ? n.href : `/${n.href}`;
  };

  const renderNavLink = (n, extraClass) => {
    const href = resolveHref(n);
    const testId = `nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`;
    if (n.isRoute) {
      return (
        <Link
          key={n.href}
          to={n.href}
          data-testid={testId}
          onClick={() => setOpen(false)}
          className={extraClass}
        >
          {n.label}
        </Link>
      );
    }
    return (
      <a
        key={n.href}
        href={href}
        data-testid={testId}
        onClick={() => setOpen(false)}
        className={extraClass}
      >
        {n.label}
      </a>
    );
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 border-b border-[#E4E7EC] backdrop-blur-xl ${
        scrolled ? "bg-white/90" : "bg-white/80"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-5 md:h-20 md:px-8">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="header-logo"
          aria-label={BRAND.name}
        >
          <span className="flex h-9 w-9 items-center justify-center bg-[#101828] font-display text-lg font-black text-[#FDB022]">
            C
          </span>
          <span className="hidden font-display text-lg font-bold leading-none tracking-tight sm:block">
            Custom Clearance
            <span className="block text-[10px] font-mono-accent font-normal tracking-[0.25em] text-[#667085]">
              PARTNER · INDIA DESK
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 md:flex" aria-label="Primary">
          {NAV.map((n) =>
            renderNavLink(
              n,
              "text-sm font-medium text-[#101828] transition-colors hover:text-[#D92D20]",
            ),
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.telHref}
            data-testid="header-call-cta"
            className="hidden items-center gap-2 border border-[#101828] bg-[#101828] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#D92D20] hover:border-[#D92D20] md:inline-flex"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            {CONTACT.phoneDisplay}
          </a>
          <button
            type="button"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex h-10 w-10 items-center justify-center border border-[#E4E7EC] bg-white md:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#E4E7EC] bg-white md:hidden" data-testid="mobile-menu">
          <nav className="mx-auto flex max-w-7xl flex-col px-5 py-4" aria-label="Mobile">
            {NAV.map((n) =>
              renderNavLink(
                n,
                "border-b border-[#E4E7EC] py-3 text-base font-medium text-[#101828]",
              ),
            )}
            <a
              href={CONTACT.telHref}
              className="mt-4 inline-flex items-center justify-center gap-2 bg-[#101828] px-4 py-3 text-sm font-semibold text-white"
              data-testid="mobile-call-cta"
            >
              <Phone className="h-4 w-4" /> {CONTACT.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
